import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Lightbulb, Play, RefreshCw, Target, VolumeX, Waves } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ChordDiagram } from '@/components/ChordDiagram';
import { LevelWave } from '@/components/LevelWave';
import { MicGate } from '@/components/MicGate';
import { Ornament } from '@/components/Ornament';
import { Button, Card } from '@/components/ui';
import { getChord } from '@/data/chords';
import { t } from '@/i18n';
import { audioEngine } from '@/audio/engine/AudioEngine';
import { useAudioState, useEngineStore } from '@/hooks/useAudio';
import { useFinishSession } from '@/hooks/useFinishSession';
import { CleanChordEngine } from '@/training/cleanChord/CleanChordEngine';
import type { StringReport } from '@/types/audio';
import type { Recommendation } from '@/types/training';
import './CleanChordPage.css';

const DEFAULT_SET = ['Am', 'C', 'G', 'Em'];

/**
 * Clean Chords: one chord, held, judged string by string — but only for the
 * strings that can honestly be judged. See `stringAnalysis.ts`.
 */
export function CleanChordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const audio = useAudioState();
  const recommendation = (location.state as { recommendation?: Recommendation } | null)?.recommendation;

  const chordIds = useMemo(() => {
    const requested = recommendation?.chordIds;
    return requested && requested.length > 0 ? requested : DEFAULT_SET;
  }, [recommendation]);

  const engineRef = useRef<CleanChordEngine | null>(null);
  const [engine, setEngine] = useState<CleanChordEngine | null>(null);

  const handleFinished = useFinishSession();

  useEffect(() => {
    if (audio.status !== 'ready' || engineRef.current) return;
    const created = new CleanChordEngine(audioEngine, chordIds, handleFinished);
    engineRef.current = created;
    setEngine(created);
    created.start();
    return () => {
      created.dispose();
      engineRef.current = null;
    };
  }, [audio.status, chordIds, handleFinished]);

  return (
    <AppShell
      title={t('clean.title')}
      showBack
      // The same rule as every other exercise: leaving saves what was played.
      // With nothing captured yet there is nothing to save — plain navigation.
      onBack={() => {
        const current = engineRef.current;
        if (current?.hasResults) current.finish();
        else navigate(-1);
      }}
    >
      <MicGate>{engine ? <CleanChordSession engine={engine} /> : null}</MicGate>
    </AppShell>
  );
}

function CleanChordSession({ engine }: { engine: CleanChordEngine }) {
  const snapshot = useEngineStore(engine.store);
  const chord = getChord(snapshot.chordId);

  const reportable = snapshot.strings.filter((report) => report.state !== 'indeterminate');

  return (
    <>
      <Card section className="clean__hero">
        <Ornament kind="leaves" position="left" size={120} />
        <div className="clean__hero-text">
          <h2 className="clean__chord">{chord?.name ?? snapshot.chordId}</h2>
          <p className="clean__chord-ru">{chord?.nameRu}</p>
        </div>
        {chord && (
          <div className="clean__hero-diagram">
            <ChordDiagram chord={chord} size={175} className="chord-diagram--accent" />
          </div>
        )}
      </Card>

      {!snapshot.captured ? (
        <>
          <div className="listening listening--on clean__listening">
            <span className="listening__badge">
              <Waves size={20} aria-hidden="true" />
            </span>
            <span className="listening__label">{t('clean.playPrompt')}</span>
            <LevelWave bars={22} height={26} />
          </div>
          {/* Quiet, not accusatory: the room made a sound, the player did not
              make a mistake. Shown only after a sustained run of non-guitar
              frames, so it cannot flicker on a single word. */}
          {snapshot.hearingOther && <p className="clean__caveat">{t('gate.hearingOther')}</p>}
        </>
      ) : (
        <>
          {reportable.length > 0 && (
            <Card padded={false} className="clean__strings">
              <ul>
                {reportable.map((report) => (
                  <StringRow key={report.string} report={report} />
                ))}
              </ul>
            </Card>
          )}

          <Card className="clean__clarity">
            <span className="clean__clarity-icon">
              <Target size={22} aria-hidden="true" />
            </span>
            <div className="clean__clarity-body">
              <p className="stat-label">{t('clean.clarity')}</p>
              <p className="clean__clarity-value tnum">{snapshot.clarity}%</p>
            </div>
            <div
              className="clean__bar"
              role="progressbar"
              aria-valuenow={snapshot.clarity}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('clean.clarity')}
            >
              <span className="clean__bar-fill" style={{ width: `${snapshot.clarity}%` }} />
            </div>
          </Card>

          <Card className="clean__fix">
            <span className="clean__fix-icon">
              <Lightbulb size={22} aria-hidden="true" />
            </span>
            <div>
              <p className="clean__fix-title">{t('clean.whatToFix')}</p>
              {snapshot.issues.length === 0 && snapshot.advice.length === 0 ? (
                <p className="muted clean__fix-empty">{t('clean.nothingToFix')}</p>
              ) : (
                <ul className="clean__fix-list">
                  {snapshot.issues.map((item, index) => (
                    <li key={`issue-${index}`}>{t(item.key, item.params)}</li>
                  ))}
                  {snapshot.advice.map((item, index) => (
                    <li key={`advice-${index}`}>{t(item.key, item.params)}</li>
                  ))}
                </ul>
              )}
              {snapshot.hasIndeterminate && (
                <p className="clean__caveat">{t('clean.someStringsUnknown')}</p>
              )}
            </div>
          </Card>
        </>
      )}

      <div className="clean__actions">
        <Button variant="ghost" onClick={() => engine.retry()}>
          <RefreshCw size={18} aria-hidden="true" />
          {t('clean.playAgain')}
        </Button>
        <Button onClick={() => engine.next()}>
          <Play size={18} fill="currentColor" aria-hidden="true" />
          {t('clean.nextChord')}
        </Button>
      </div>
    </>
  );
}

function StringRow({ report }: { report: StringReport }) {
  const config = TONE[report.state] ?? TONE.clear;
  return (
    <li className="clean__string">
      <span className={`clean__string-badge clean__string-badge--${config.tone}`}>{config.icon}</span>
      <span className="clean__string-label">{t(config.key, { string: report.string })}</span>
      <span className={`clean__string-wave clean__string-wave--${config.tone}`} aria-hidden="true">
        {WAVE_BARS.map((height, index) => (
          <span key={index} style={{ height: `${height}%` }} />
        ))}
      </span>
    </li>
  );
}

const WAVE_BARS = [28, 55, 90, 62, 100, 45, 72, 34];

const TONE = {
  clear: {
    tone: 'good' as const,
    key: 'feedback.stringClear' as const,
    icon: <Check size={18} aria-hidden="true" />,
  },
  weak: {
    tone: 'warn' as const,
    key: 'feedback.stringWeak' as const,
    icon: <Waves size={18} aria-hidden="true" />,
  },
  silent: {
    tone: 'bad' as const,
    key: 'feedback.stringSilent' as const,
    icon: <VolumeX size={18} aria-hidden="true" />,
  },
  unwanted: {
    tone: 'bad' as const,
    key: 'feedback.stringUnwanted' as const,
    icon: <VolumeX size={18} aria-hidden="true" />,
  },
  indeterminate: {
    tone: 'muted' as const,
    key: 'feedback.stringClear' as const,
    icon: <Waves size={18} aria-hidden="true" />,
  },
};
